import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DemoBadge } from "@/components/primitives";
import { OnboardingTour } from "@/components/OnboardingTour";
import RankCelebration from "@/components/RankCelebration";
import {
  SquaresFour, Cpu, IdentificationBadge, CreditCard, Receipt, Planet, ArrowLineDown,
  UsersThree, Trophy, BookOpen, ShieldCheck, UserCircle, SignOut, House, List, X,
  CaretLeft, DotsThreeOutline, WarningCircle, ArrowClockwise, Bell, Broadcast, GraduationCap, Sun, Moon, Question,
} from "@phosphor-icons/react";

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const ref = React.useRef(null);
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onClick); };
  }, []);
  const markAll = async () => { await api.post("/notifications/read", { all: true }); load(); };
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid="notif-bell" className="relative grid place-items-center h-9 w-9 rounded-full border border-white/10 text-[#94A3B8] hover:text-white hover:border-white/20 transition-colors">
        <Bell size={18} weight={data.unread ? "fill" : "regular"} />
        {data.unread > 0 && <span data-testid="notif-count" className="absolute -top-1 -right-1 grid place-items-center h-4 min-w-4 px-1 rounded-full bg-[#2DD4BF] text-[#030305] text-[10px] font-bold">{data.unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full pt-2 w-80 z-50" data-testid="notif-panel">
          <div className="qs-glass rounded-xl border border-white/10 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <span className="text-sm font-bold text-white">Notifications</span>
              {data.unread > 0 && <button onClick={markAll} className="text-xs text-[#2DD4BF] hover:text-white">Mark all read</button>}
            </div>
            <div className="max-h-96 overflow-y-auto divide-y divide-white/5">
              {data.items.length ? data.items.map((n) => (
                <div key={n.id} className={`px-4 py-3 ${!n.read ? "bg-[#2DD4BF]/[0.04]" : ""}`}>
                  <div className="flex items-center gap-2">
                    {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-[#2DD4BF]" />}
                    <p className="text-sm text-white">{n.title}</p>
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-0.5">{n.body}</p>
                </div>
              )) : <p className="px-4 py-8 text-center text-sm text-[#64748B] font-mono">No notifications.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const NAV = [
  { to: "/dashboard", label: "Overview", icon: SquaresFour, end: true },
  { to: "/dashboard/q-core", label: "Q-Core", icon: Cpu },
  { to: "/dashboard/signals", label: "Quantum Signals", icon: Broadcast },
  { to: "/dashboard/trainings", label: "VIP Trainings", icon: GraduationCap },
  { to: "/dashboard/quantum-verse", label: "Quantum Verse", icon: Planet },
  { to: "/dashboard/membership", label: "Membership", icon: IdentificationBadge },
  { to: "/dashboard/activities", label: "Activities", icon: Receipt },
  { to: "/dashboard/withdrawals", label: "Withdrawals", icon: ArrowLineDown },
  { to: "/dashboard/referrals", label: "Referrals", icon: UsersThree },
  { to: "/dashboard/quantum-flex", label: "Quantum Flex", icon: Trophy },
  { to: "/dashboard/virtual-card", label: "Virtual Card", icon: CreditCard },
  { to: "/dashboard/resources", label: "Resources", icon: BookOpen },
  { to: "/dashboard/support", label: "Support", icon: Question },
  { to: "/dashboard/security", label: "Security", icon: ShieldCheck },
  { to: "/dashboard/profile", label: "Account Profile", icon: UserCircle },
];

const MOBILE = [
  { to: "/dashboard", label: "Overview", icon: SquaresFour, end: true },
  { to: "/dashboard/q-core", label: "Q-Core", icon: Cpu },
  { to: "/dashboard/quantum-verse", label: "Verse", icon: Planet },
  { to: "/dashboard/withdrawals", label: "Withdraw", icon: ArrowLineDown },
];

function RenewalBanner() {
  const [lic, setLic] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { api.get("/license/me").then((r) => setLic(r.data)).catch(() => {}); }, []);
  if (dismissed || !lic || (lic.state !== "expiring_soon" && lic.state !== "expired")) return null;
  const r = lic.reminders?.[0];
  const crit = ["expired", "critical", "urgent"].includes(r?.level);
  return (
    <div data-testid="renewal-banner" className={`flex items-center gap-3 px-5 py-2.5 border-b ${crit ? "border-red-500/30 bg-red-500/[0.08]" : "border-amber-400/30 bg-amber-400/[0.08]"}`}>
      <WarningCircle size={18} weight="fill" className={crit ? "text-red-400" : "text-amber-400"} />
      <p className="flex-1 text-xs sm:text-sm text-[#e2e8f0] truncate">{r?.message}</p>
      <button onClick={() => navigate("/dashboard/membership")} className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${crit ? "bg-red-500 text-white" : "bg-amber-400 text-[#030305]"}`}>
        <ArrowClockwise size={13} weight="bold" /> Renew
      </button>
      <button onClick={() => setDismissed(true)} className="text-[#94A3B8] hover:text-white"><X size={15} /></button>
    </div>
  );
}

export default function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const { user, logout } = useAuth();
  const [theme, setTheme] = useState(() => localStorage.getItem("qs_theme") || "dark");
  useEffect(() => { localStorage.setItem("qs_theme", theme); }, [theme]);
  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { setMobileMenu(false); }, [location.pathname]);

  const doLogout = async () => { await logout(); navigate("/"); };
  const current = NAV.find((n) => n.end ? location.pathname === n.to : location.pathname.startsWith(n.to) && n.to !== "/dashboard") || NAV[0];

  const SidebarLink = ({ n }) => (
    <NavLink
      to={n.to}
      end={n.end}
      data-testid={`dnav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
          isActive ? "bg-[#2DD4BF]/10 text-[#2DD4BF] border border-[#2DD4BF]/20" : "text-[#94A3B8] hover:text-white hover:bg-white/5 border border-transparent"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <n.icon size={19} weight={isActive ? "fill" : "regular"} className="shrink-0" />
          {!collapsed && <span className="truncate">{n.label}</span>}
        </>
      )}
    </NavLink>
  );

  return (
    <div data-theme={theme} className="min-h-screen bg-[#030305] text-white flex">
      {/* Quantum-core ambient background */}
      <div data-bg-ambient className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute inset-0 bg-center bg-cover opacity-[0.14]"
          style={{ backgroundImage: "url('/quantum-bg.jpg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#030305]/70 via-[#030305]/85 to-[#030305]" />
      </div>
      <OnboardingTour />
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex ${collapsed ? "w-[76px]" : "w-64"} shrink-0 flex-col border-r border-white/10 bg-[#08090F] fixed inset-y-0 transition-[width] duration-300 z-20`} data-testid="dashboard-sidebar">
        <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2.5 overflow-hidden">
            {collapsed ? (
              <span className="grid place-items-center h-8 w-8 rounded-md border border-[#2DD4BF]/40 bg-[#2DD4BF]/10 shrink-0">
                <span className="h-3 w-3 rounded-sm bg-[#2DD4BF] rotate-45" />
              </span>
            ) : (
              <img src="/logo.png" alt="Quantum Scalp" className="h-8 w-auto" />
            )}
          </Link>
          <button onClick={() => setCollapsed(!collapsed)} className="text-[#64748B] hover:text-white" aria-label="Toggle sidebar">
            <CaretLeft size={16} className={`transition-transform ${collapsed ? "rotate-180" : ""}`} />
          </button>
        </div>
        {!collapsed ? (
          <div className="mx-2.5 mt-3 rounded-lg border border-[#00E676]/20 bg-[#00E676]/[0.05] px-3 py-2.5" data-testid="qcore-status">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Q-Core Status</span>
              <span className="qs-pulse-dot h-2 w-2 rounded-full bg-[#00E676] text-[#00E676]" />
            </div>
            <p className="mt-1 font-mono text-xs text-[#00E676]">OPERATIONAL</p>
            <p className="text-[10px] text-slate-500 mt-0.5">Market · Execution · Chain</p>
          </div>
        ) : (
          <div className="flex justify-center mt-3" data-testid="qcore-status-mini">
            <span className="qs-pulse-dot h-2.5 w-2.5 rounded-full bg-[#00E676] text-[#00E676]" />
          </div>
        )}
        <nav className="flex-1 p-2.5 space-y-1 overflow-y-auto">
          {NAV.map((n) => <SidebarLink key={n.to} n={n} />)}
        </nav>
        <div className="p-2.5 border-t border-white/10 space-y-1">
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#94A3B8] hover:text-white hover:bg-white/5"><House size={19} />{!collapsed && "Public Site"}</Link>
          <button onClick={toggleTheme} data-testid="theme-toggle" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#94A3B8] hover:text-white hover:bg-white/5">{theme === "dark" ? <Sun size={19} /> : <Moon size={19} />}{!collapsed && (theme === "dark" ? "Light Mode" : "Dark Mode")}</button>
          <button onClick={doLogout} data-testid="dash-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#94A3B8] hover:text-white hover:bg-white/5"><SignOut size={19} />{!collapsed && "Logout"}</button>
        </div>
      </aside>

      <div className={`relative z-10 flex-1 ${collapsed ? "md:ml-[76px]" : "md:ml-64"} transition-[margin] duration-300 pb-20 md:pb-0`}>
        {/* Topbar */}
        <header className="h-16 border-b border-white/10 flex items-center justify-between px-4 md:px-8 sticky top-0 bg-[#030305]/85 backdrop-blur-xl z-10">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-white" onClick={() => setMobileMenu(true)} aria-label="Menu"><List size={22} /></button>
            <h1 className="text-base md:text-lg font-bold">{current?.label}</h1>
          </div>
          <div className="flex items-center gap-3">
            <DemoBadge className="hidden lg:inline-flex" />
            <NotificationBell />
            <span className="grid place-items-center h-9 w-9 rounded-full bg-[#2DD4BF]/15 text-[#2DD4BF] font-bold text-sm">{user?.name?.[0]?.toUpperCase() || "M"}</span>
          </div>
        </header>

        <RenewalBanner />

        <main className="p-4 md:p-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 qs-glass border-t border-white/10 grid grid-cols-5">
        {MOBILE.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] ${isActive ? "text-[#2DD4BF]" : "text-[#94A3B8]"}`}>
            <n.icon size={20} /> {n.label}
          </NavLink>
        ))}
        <button onClick={() => setMobileMenu(true)} className="flex flex-col items-center gap-1 py-2.5 text-[10px] text-[#94A3B8]">
          <DotsThreeOutline size={20} /> More
        </button>
      </nav>

      {/* Mobile drawer */}
      {mobileMenu && (
        <div className="md:hidden fixed inset-0 z-40" onClick={() => setMobileMenu(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute right-0 inset-y-0 w-72 bg-[#08090F] border-l border-white/10 p-4 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <span className="font-black">Menu</span>
              <button onClick={() => setMobileMenu(false)} className="text-[#94A3B8]"><X size={20} /></button>
            </div>
            <div className="space-y-1">
              {NAV.map((n) => <SidebarLink key={n.to} n={n} />)}
              <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#94A3B8]"><SignOut size={19} /> Logout</button>
            </div>
          </div>
        </div>
      )}
      <RankCelebration />

    </div>
  );
}
